import React from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import { ArrowRight, ArrowLeft, Sparkles, CheckCircle, Clock, MapPin, Calendar } from 'lucide-react';
import { motion } from 'framer-motion';

const PROGRAMS = {
  kids: {
    name: 'Kids Robotics', age: '5-7 ANS', tagColor: 'bg-emerald-500', heroColor: 'from-emerald-500 to-[#1B6FE3]',
    logo: 'https://horizons-cdn.hostinger.com/1b0ab6fa-0746-4a31-a6ec-811cdb30e480/e62999d5ca87cb2ae11e329b42f06396.png',
    description: "Une introduction douce et ludique à la robotique pour éveiller la curiosité des plus petits à travers des jeux interactifs.",
    why: "À cet âge, les enfants sont de véritables éponges. Le programme Kids Robotics utilise leur curiosité naturelle pour introduire les concepts de base de la logique et de la technologie sans écran prolongé, en privilégiant la manipulation physique et le jeu.",
    objectives: [
      { title: 'Motricité fine', desc: "Développer l'habileté manuelle par l'assemblage de pièces adaptées." },
      { title: 'Logique séquentielle', desc: "Comprendre l'ordre des actions (début, milieu, fin) pour atteindre un but." },
      { title: 'Éveil technologique', desc: "Démystifier les robots et comprendre qu'ils obéissent à nos commandes." },
    ],
    activities: ["Construction de modèles simples avec des briques LEGO® Education", "Programmation sans écran avec des robots de sol (Bee-Bot, Cubetto)", "Jeux de rôle pour comprendre les algorithmes", "Création de petites histoires animées"],
    skills: ['Repérage dans l\'espace', 'Concentration', 'Travail en binôme', 'Créativité'],
    schedule: [
      { week: 'S1', title: 'Découverte des robots', desc: "Qu'est-ce qu'un robot ? Comment communique-t-on avec lui ?" },
      { week: 'S2', title: 'Premières constructions', desc: "Assemblage de mécanismes simples (engrenages, poulies)." },
      { week: 'S3', title: "Le parcours d'obstacles", desc: "Programmer un robot pour qu'il se déplace sur un tapis thématique." },
      { week: 'S4', title: 'Mon premier projet', desc: "Création libre et présentation aux autres enfants." },
    ],
    duration: '1h30 par semaine', session: 'Mercredi ou Samedi matin', lieu: 'Centre Ludotech Robotics ou Écoles partenaires', tarif: '45.000 FCFA/ mois',
  },
  neo: {
    name: 'Néo Robotics', age: '8-10 ANS', tagColor: 'bg-[#1B6FE3]', heroColor: 'from-[#1B6FE3] to-indigo-600',
    logo: 'https://horizons-cdn.hostinger.com/1b0ab6fa-0746-4a31-a6ec-811cdb30e480/16bee4967de0f599f2b9b90524124300.png',
    description: "Apprentissage des bases de la construction et de la programmation avec des robots simples et amusants.",
    why: "C'est l'âge idéal pour passer de la simple manipulation à la véritable création. Le programme Néo Robotics permet aux enfants de comprendre comment les capteurs et les moteurs interagissent grâce au code qu'ils créent eux-mêmes.",
    objectives: [
      { title: 'Programmation par blocs', desc: "Maîtriser les boucles, les conditions et les variables avec Scratch." },
      { title: 'Mécanique de base', desc: "Comprendre le fonctionnement des capteurs (distance, couleur, toucher)." },
      { title: 'Collaboration', desc: "Apprendre à se répartir les tâches dans un projet commun." },
    ],
    activities: ["Construction de robots motorisés (LEGO® Spike Essential, mBot)", "Programmation visuelle sur tablette ou ordinateur", "Résolution de défis chronométrés", "Création de jeux vidéo simples"],
    skills: ['Pensée algorithmique', 'Résolution de problèmes', 'Compréhension mécanique', 'Persévérance'],
    schedule: [
      { week: 'S1', title: 'Les bases du code', desc: "Introduction à l'interface de programmation et premiers scripts." },
      { week: 'S2', title: 'Capteurs en action', desc: "Programmer le robot pour réagir à son environnement." },
      { week: 'S3', title: 'Le défi du labyrinthe', desc: "Combiner mouvements et capteurs pour sortir d'un labyrinthe." },
      { week: 'S4', title: "Projet d'équipe", desc: "Conception d'un robot utile pour la vie quotidienne." },
    ],
    duration: '1h par semaine', session: 'Mercredi ou Samedi', lieu: 'Centre LudoTech Robotics', tarif: 'A partir de 3000 FCFA',
  },
  ruby: {
    name: 'Ruby Robotics', age: '11-13 ANS', tagColor: 'bg-[#F97316]', heroColor: 'from-[#F97316] to-red-500',
    logo: 'https://horizons-cdn.hostinger.com/1b0ab6fa-0746-4a31-a6ec-811cdb30e480/f03d1135ae423dd5c6f40271abe483d7.png',
    description: "Découverte des langages de programmation pour donner vie à des créations robotiques plus complexes.",
    why: "À l'entrée au collège, les jeunes ont besoin de défis plus stimulants. Ruby Robotics fait le pont entre la programmation visuelle et textuelle, en introduisant des concepts d'ingénierie et de développement d'applications.",
    objectives: [
      { title: 'Transition vers le code', desc: "Passer progressivement des blocs au code textuel (Python basique)." },
      { title: "Création d'applications", desc: "Développer des applications mobiles simples pour contrôler les robots." },
      { title: 'Électronique ludique', desc: "Découvrir les circuits avec des cartes micro-contrôleurs (Micro:bit)." },
    ],
    activities: ["Programmation de cartes Micro:bit", "Création d'applications Android avec App Inventor", "Construction de robots avancés (LEGO® Spike Prime)", "Initiation à la modélisation 3D"],
    skills: ['Logique de programmation avancée', 'Bases en électronique', "Design d'interface (UI)", 'Gestion de projet'],
    schedule: [
      { week: 'S1', title: 'Découverte Micro:bit', desc: "Programmer une carte électronique avec des LEDs et des boutons." },
      { week: 'S2', title: 'Communication sans fil', desc: "Faire communiquer deux cartes entre elles par radio." },
      { week: 'S3', title: 'Mon appli mobile', desc: "Créer une interface sur smartphone pour piloter un système." },
      { week: 'S4', title: 'Le robot connecté', desc: "Intégrer la carte, les moteurs et l'application dans un projet global." },
    ],
    duration: '1h par semaine', session: 'Mercredi ou Samedi', lieu: 'Centre LudoTech Robotics', tarif: 'A partir de 3000 FCFA',
  },
  archy: {
    name: 'Archy Robotics', age: '14-15 ANS', tagColor: 'bg-purple-500', heroColor: 'from-purple-500 to-indigo-600',
    logo: 'https://horizons-cdn.hostinger.com/1b0ab6fa-0746-4a31-a6ec-811cdb30e480/09457e63cf1ee7e6ea4c6cd6defd7fc3.png',
    description: "Conception de structures robotiques avancées et introduction à la mécanique et l'ingénierie.",
    why: "Pour les adolescents, la technologie doit être un outil de création sans limite. Archy Robotics les plonge dans le monde des 'Makers' avec l'utilisation d'Arduino, l'impression 3D et la programmation textuelle.",
    objectives: [
      { title: 'Ingénierie matérielle', desc: "Concevoir et assembler des circuits électroniques sur breadboard." },
      { title: 'Code textuel', desc: "Programmer en C++ (Arduino) et Python." },
      { title: 'Prototypage rapide', desc: "Utiliser la modélisation et l'impression 3D pour créer des pièces sur mesure." },
    ],
    activities: ["Câblage de composants électroniques (LEDs, servomoteurs, capteurs ultrasons)", "Programmation de microcontrôleurs Arduino", "Modélisation 3D sur Tinkercad ou Fusion 360", "Création de systèmes domotiques (Smart Home)"],
    skills: ['Programmation C++ / Python', 'Électronique et câblage', 'Modélisation 3D', 'Autonomie technique'],
    schedule: [
      { week: 'S1', title: "L'univers Arduino", desc: "Comprendre le circuit électrique et écrire son premier code en C++." },
      { week: 'S2', title: 'Capteurs avancés', desc: "Utiliser des capteurs de température, d'humidité et de mouvement." },
      { week: 'S3', title: 'Design 3D', desc: "Modéliser le boîtier de son projet et lancer l'impression 3D." },
      { week: 'S4', title: 'Projet Maker', desc: "Assemblage final du prototype (ex: station météo connectée, bras robotisé)." },
    ],
    duration: '1h par semaine', session: 'Mercredi ou Samedi', lieu: 'Centre LudoTech Robotics', tarif: 'A partir de 3000 FCFA',
  },
  elite: {
    name: 'Elite Robotics', age: '16-17 ANS', tagColor: 'bg-red-500', heroColor: 'from-red-600 to-slate-900',
    logo: 'https://horizons-cdn.hostinger.com/1b0ab6fa-0746-4a31-a6ec-811cdb30e480/6850637e148d6c254be897802a8ef336.png',
    description: "Maîtrise approfondie de la robotique, programmation textuelle et préparation aux compétitions nationales.",
    why: "Le programme ultime pour les futurs ingénieurs et développeurs. Elite Robotics aborde des concepts de niveau universitaire adaptés aux lycéens, incluant l'intelligence artificielle, la vision par ordinateur et la préparation aux concours de robotique.",
    objectives: [
      { title: 'Intelligence Artificielle', desc: "Initiation au Machine Learning et à la reconnaissance d'images." },
      { title: 'Systèmes embarqués', desc: "Utilisation de Raspberry Pi pour des projets IoT complexes." },
      { title: 'Esprit de compétition', desc: "Préparation technique et stratégique pour les concours nationaux." },
    ],
    activities: ["Programmation avancée en Python", "Configuration et utilisation de Raspberry Pi", "Projets de vision par ordinateur (OpenCV)", "Conception de robots autonomes de compétition"],
    skills: ['Architecture système (Linux)', 'Algorithmique complexe', "Bases de l'Intelligence Artificielle", 'Gestion du stress et stratégie'],
    schedule: [
      { week: 'S1', title: 'Systèmes Linux & Python', desc: "Prise en main du Raspberry Pi et scripts Python avancés." },
      { week: 'S2', title: 'Vision par ordinateur', desc: "Programmer une caméra pour reconnaître des formes et des couleurs." },
      { week: 'S3', title: 'Robotique autonome', desc: "Combiner vision et moteurs pour un déplacement 100% autonome." },
      { week: 'S4', title: 'Simulation de concours', desc: "Mise en situation réelle sur un cahier des charges de compétition." },
    ],
    duration: '1h par semaine', session: 'Mercredi ou Samedi', lieu: 'Centre LudoTech Robotics', tarif: 'A partir de 3000 FCFA',
  },
};

export const ProgramDetailPage = () => {
  const { slug } = useParams();
  const prog = PROGRAMS[slug];

  if (!prog) {
    return (
      <Layout>
        <div className="py-20 text-center">
          <p className="text-slate-600">Programme non trouvé</p>
          <Link to="/programmes" className="text-[#1B6FE3] hover:underline mt-4 inline-block font-bold">Retour aux programmes</Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* HERO */}
      <section className={`bg-gradient-to-br ${prog.heroColor} py-16 md:py-24 text-center`} data-testid="program-detail-hero">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm px-5 py-2 rounded-full text-sm font-bold text-white/90 mb-5">
              <Sparkles size={14} /> PROGRAMME {prog.age}
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-4">{prog.name}</h1>
            <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto mb-8">{prog.description}</p>
            <Link to="/contact" className="btn-primary text-base px-8 py-4 inline-flex items-center gap-2">
              Réserver une place <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* POURQUOI */}
      <section className="py-16 md:py-20 bg-white" data-testid="why-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-3xl text-center">
          <h2 className="font-heading text-3xl font-extrabold text-slate-900 mb-5">Pourquoi ce programme ?</h2>
          <p className="text-slate-600 leading-relaxed">{prog.why}</p>
        </div>
      </section>

      {/* OBJECTIFS */}
      <section className="py-16 md:py-20 section-light-blue" data-testid="objectives-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <h2 className="font-heading text-3xl font-extrabold text-slate-900 text-center mb-3">Objectifs pédagogiques</h2>
          <p className="text-slate-600 text-center mb-10">Ce que votre enfant va acquérir tout au long du parcours.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-4xl mx-auto">
            {prog.objectives.map((obj, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.1 }} viewport={{ once: true }} className="tk-card text-center">
                <div className={`${prog.tagColor} w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3 text-white font-bold text-lg`}>{i + 1}</div>
                <h3 className="font-heading text-lg font-extrabold text-slate-900 mb-1.5">{obj.title}</h3>
                <p className="text-slate-600 text-sm">{obj.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CE QUE L'ENFANT VA FAIRE + COMPÉTENCES */}
      <section className="py-16 md:py-20 bg-white" data-testid="activities-skills-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div>
              <h2 className="font-heading text-2xl font-extrabold text-slate-900 mb-5">Ce que l'enfant va faire</h2>
              <ul className="space-y-3">
                {prog.activities.map((act, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <CheckCircle size={18} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700 text-sm">{act}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="font-heading text-2xl font-extrabold text-slate-900 mb-5">Compétences développées</h2>
              <div className="flex flex-wrap gap-2">
                {prog.skills.map((skill, i) => (
                  <span key={i} className="bg-blue-50 text-[#1B6FE3] font-bold text-sm px-4 py-2.5 rounded-full">{skill}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DÉROULEMENT */}
      <section className="py-16 md:py-20 section-cream" data-testid="schedule-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <h2 className="font-heading text-3xl font-extrabold text-slate-900 text-center mb-3">Déroulement du programme</h2>
          <p className="text-slate-600 text-center mb-10">Une progression pédagogique structurée sur plusieurs semaines.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-5xl mx-auto">
            {prog.schedule.map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: i * 0.1 }} viewport={{ once: true }} className="tk-card text-center">
                <div className={`${prog.tagColor} text-white text-xs font-bold px-3 py-1 rounded-full inline-block mb-3`}>{s.week}</div>
                <h3 className="font-heading text-base font-extrabold text-slate-900 mb-1.5">{s.title}</h3>
                <p className="text-slate-600 text-xs">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* INFOS PRATIQUES */}
      <section className="py-16 md:py-20 bg-white" data-testid="info-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-3xl">
          <h2 className="font-heading text-3xl font-extrabold text-slate-900 text-center mb-8">Informations Pratiques</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="tk-card text-center">
              <Clock size={20} className="mx-auto text-[#1B6FE3] mb-2" />
              <p className="text-xs text-slate-500 mb-1">Durée</p>
              <p className="font-heading text-sm font-extrabold text-slate-900">{prog.duration}</p>
            </div>
            <div className="tk-card text-center">
              <Calendar size={20} className="mx-auto text-[#F97316] mb-2" />
              <p className="text-xs text-slate-500 mb-1">Session</p>
              <p className="font-heading text-sm font-extrabold text-slate-900">{prog.session}</p>
            </div>
            <div className="tk-card text-center">
              <MapPin size={20} className="mx-auto text-emerald-500 mb-2" />
              <p className="text-xs text-slate-500 mb-1">Lieu</p>
              <p className="font-heading text-sm font-extrabold text-slate-900">{prog.lieu}</p>
            </div>
          </div>

          <div className="tk-card bg-blue-50 border-blue-100 text-center">
            <p className="text-xs text-slate-500 mb-1">Tarif</p>
            <p className="font-heading text-3xl font-black text-[#1B6FE3]">{prog.tarif}</p>
            <p className="text-xs text-slate-500 mt-2">Matériel inclus. Places limitées à 10 enfants par session pour un suivi optimal.</p>
          </div>

          <div className="text-center mt-8">
            <Link to="/contact" className="btn-primary text-base px-10 py-4 inline-flex items-center gap-2">
              S'inscrire maintenant <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className={`py-16 md:py-20 bg-gradient-to-br ${prog.heroColor}`} data-testid="cta-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-black text-white mb-5">Offrez à votre enfant les compétences du futur</h2>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/contact" className="btn-primary text-base px-8 py-4 inline-flex items-center gap-2">
              Réserver un essai gratuit <ArrowRight size={18} />
            </Link>
            <Link to="/programmes" className="bg-white/15 backdrop-blur-sm border-2 border-white/30 text-white px-8 py-4 rounded-full font-bold text-base hover:bg-white/25 transition-all text-center inline-flex items-center gap-2">
              <ArrowLeft size={16} /> Voir tous les programmes
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ProgramDetailPage;
